<script setup>
import { ref, watch, onBeforeUnmount } from 'vue'
import { useToast } from '@/composables/useToast'

const { toast, clearToast } = useToast()
const toastActive = ref(false)
let showTimer = null
let hideTimer = null

const clearTimers = () => {
  if (showTimer) {
    clearTimeout(showTimer)
    showTimer = null
  }
  if (hideTimer) {
    clearTimeout(hideTimer)
    hideTimer = null
  }
}

const closeToast = () => {
  clearTimers()
  toastActive.value = false
  clearToast()
}

watch(
  toast,
  (newToast) => {
    clearTimers()
    toastActive.value = false
    if (newToast && newToast.title) {
      showTimer = setTimeout(() => {
        toastActive.value = true
      }, 200)

      hideTimer = setTimeout(() => {
        closeToast()
      }, 4000)
    }
  },
  { immediate: false },
)

onBeforeUnmount(() => {
  clearTimers()
  toastActive.value = false
  clearToast()
})
</script>

<template>
  <transition name="slide-fade">
    <div
      v-if="toastActive && toast"
      :class="['toast-wrapper', toast.mode]">
      <svg
        @click="closeToast"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
        class="close-icon">
        <path
          d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
      </svg>
      <h2>{{ toast.title }}</h2>
      <p v-if="toast.message">{{ toast.message }}</p>
    </div>
  </transition>
</template>

<style scoped>
.slide-fade-enter-active {
  transition: all 0.2s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

.slide-fade-enter-to,
.slide-fade-leave-from {
  transform: translateY(0);
  opacity: 1;
}

.toast-wrapper {
  pointer-events: all;
  position: absolute;
  width: 40%;
  max-width: 500px;
  border-radius: 0 0 1rem 1rem;
  background-color: var(--background-reverse);
  color: var(--background-color);
  padding: 2rem;
  font-family: var(--title-font);
  box-shadow:
    2px 2px 2px rgba(0, 0, 0, 0.248),
    inset 0 2px 1px rgba(0, 0, 0, 0.388);
}

h2 {
  font-weight: var(--font-bold);
  font-size: 1.2rem;
}

.close-icon {
  position: absolute;
  width: 30px;
  right: 0;
  top: 0;
  margin: 8px;
  cursor: pointer;
}

.neutral {
  background-color: var(--background-reverse);
}

.success {
  background-color: var(--primary-color);
  color: var(--white);
}

.warning {
  background-color: var(--warning-background);
  color: var(--black);
}

.failure {
  background-color: var(--error-background);
  color: var(--white);
}

@media screen and (max-width: 768px) {
  .toast-wrapper {
    max-width: 80dvw;
    width: 80dvw;
  }
}
</style>
